import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import styled from '@emotion/styled';
import { uiTheme as t, FormDrawer, SegmentedControl } from 'Components/ui';
import { Instructor, InstructorDraft, InstructorStatus } from '../data/instructors';
import { SaveResult } from '../hooks/useInstructors';
import { instructorFormSchema, InstructorFormValues } from '../schemas/instructor-form-schema';
import FormField from './form/FormField';
import TextInput from './form/TextInput';

export interface InstructorFormLabels {
  title: string;
  subtitle: string;
  close: string;
  name: string;
  namePlaceholder: string;
  email: string;
  emailPlaceholder: string;
  phone: string;
  phonePlaceholder: string;
  organization: string;
  organizationPlaceholder: string;
  status: string;
  statusActive: string;
  statusInactive: string;
  cancel: string;
  save: string;
}

interface InstructorFormDrawerProps {
  open: boolean;
  /** Instructor en edición, o `null` para alta. */
  editing: Instructor | null;
  labels: InstructorFormLabels;
  onClose: () => void;
  onSubmit: (draft: InstructorDraft) => Promise<SaveResult>;
}

const emptyValues: InstructorFormValues = {
  name: '',
  email: '',
  phone: '',
  organization: '',
  status: 'active',
};

const toFormValues = (instructor: Instructor | null): InstructorFormValues =>
  instructor
    ? {
        name: instructor.name,
        email: instructor.email,
        phone: instructor.phone,
        organization: instructor.organization,
        status: instructor.status,
      }
    : emptyValues;

const FieldPair = styled.div({
  display: 'flex',
  gap: 14,
  '& > *': { flex: 1, minWidth: 0 },
});

/**
 * Organismo: panel lateral de alta y edición de un instructor.
 *
 * La validación corre con react-hook-form + zod (`instructorFormSchema`).
 * El correo y el teléfono validan formato; los errores se muestran bajo cada
 * campo y marcan el borde en rojo. El tipo de instructor no se pide: todos son
 * externos y se fija en el envío.
 */
const InstructorFormDrawer = ({ open, editing, labels, onClose, onSubmit }: InstructorFormDrawerProps) => {
  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<InstructorFormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: zodResolver(instructorFormSchema),
    defaultValues: emptyValues,
  });

  const [submitting, setSubmitting] = useState(false);

  // Reinicia el formulario al abrir, según se trate de alta o edición.
  useEffect(() => {
    if (open) reset(toFormValues(editing));
    // Solo reaccionamos a la apertura; `editing` se captura en ese instante.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async (values: InstructorFormValues) => {
    setSubmitting(true);
    // Construimos el draft explícitamente (con `strict: false` la inferencia de
    // zod marca los campos como opcionales y el spread no encaja en InstructorDraft).
    // Todos los instructores son externos; al editar se conserva su tipo.
    const result = await onSubmit({
      name: values.name,
      email: values.email,
      phone: values.phone,
      organization: values.organization,
      status: values.status,
      type: editing?.type ?? 'external',
    });
    setSubmitting(false);
    // El correo duplicado (409) se muestra como error del propio campo.
    if (result?.emailTaken) {
      setError('email', { type: 'server', message: result.message });
    }
  };

  return (
    <FormDrawer
      open={open}
      formId="instructor-form"
      title={labels.title}
      subtitle={labels.subtitle}
      labels={{ close: labels.close, cancel: labels.cancel, submit: labels.save }}
      submitting={submitting}
      onClose={onClose}
      onSubmit={handleSubmit(submit)}
    >
      <Controller
        name="name"
        control={control}
        render={({ field }) => (
          <FormField label={labels.name} error={errors.name?.message}>
            <TextInput
              value={field.value}
              placeholder={labels.namePlaceholder}
              onChange={field.onChange}
              onBlur={field.onBlur}
              invalid={!!errors.name}
            />
          </FormField>
        )}
      />

      <FieldPair>
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <FormField label={labels.email} error={errors.email?.message}>
              <TextInput
                value={field.value}
                placeholder={labels.emailPlaceholder}
                onChange={field.onChange}
                onBlur={field.onBlur}
                invalid={!!errors.email}
              />
            </FormField>
          )}
        />
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <FormField label={labels.phone} error={errors.phone?.message}>
              <TextInput
                value={field.value}
                placeholder={labels.phonePlaceholder}
                onChange={field.onChange}
                onBlur={field.onBlur}
                invalid={!!errors.phone}
              />
            </FormField>
          )}
        />
      </FieldPair>

      <Controller
        name="organization"
        control={control}
        render={({ field }) => (
          <FormField label={labels.organization} error={errors.organization?.message}>
            <TextInput
              value={field.value}
              placeholder={labels.organizationPlaceholder}
              onChange={field.onChange}
              onBlur={field.onBlur}
            />
          </FormField>
        )}
      />

      <Controller
        name="status"
        control={control}
        render={({ field }) => (
          <FormField label={labels.status}>
            <SegmentedControl<InstructorStatus>
              value={field.value}
              onChange={field.onChange}
              options={[
                { value: 'active', label: labels.statusActive, activeColor: t.color.statusActive },
                { value: 'inactive', label: labels.statusInactive, activeColor: t.color.danger },
              ]}
            />
          </FormField>
        )}
      />
    </FormDrawer>
  );
};

export default InstructorFormDrawer;
